import { useState, useEffect } from 'react';
// Removed apiService import to avoid build issues
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import {
  User,
  Mail,
  Phone,
  Calendar,
  MapPin,
  Shield,
  Camera,
  Clock,
  Edit,
  Save,
  Upload,
  Key,
  Building,
  Home,
  CheckCircle
} from 'lucide-react';

interface ProfileProps {
  onNavigateToTab?: (tab: string) => void;
  onAddAlert: (alertData: any) => void;
}

// Local API request function to avoid import issues
const makeApiRequest = async (endpoint: string, options: RequestInit = {}): Promise<any> => {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8006';
  const token = localStorage.getItem('csense_auth_token');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const url = `${API_BASE_URL}${endpoint}`;
  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
  }

  return response.json();
};

export function Profile({ onNavigateToTab, onAddAlert }: ProfileProps) {
  const [isEditing, setIsEditing] = useState(false);

  // Load user data on mount
  useEffect(() => {
    const loadUserData = async () => {
      try {
        const userData = await makeApiRequest('/api/auth/me', {});
        console.log('Profile user data:', userData);

        setProfileData({
          firstName: userData.first_name || '',
          lastName: userData.last_name || '',
          email: userData.email || '',
          phone: userData.phone || '',
          role: 'User',
          organization: userData.organization_name || '',
          organizationType: userData.organization_type || 'organization',
          joinDate: userData.created_at ? new Date(userData.created_at).toISOString().split('T')[0] : '',
          lastLogin: new Date().toISOString(),
          address: userData.address || ''
        });

        // Load stats
        try {
          const agentStatus = await makeApiRequest('/api/stats', {});
          setStats({
            camerasManaged: agentStatus.total_cameras || 0,
            alertsHandled: 0,
            systemUptime: '99.8%',
            totalEvents: 0
          });
        } catch (statsError) {
          console.error('Failed to load stats:', statsError);
          // Keep default stats
        }
      } catch (error) {
        console.error('Failed to load user data:', error);
        onAddAlert({
          type: 'error',
          title: 'Profile Load Failed',
          message: 'Failed to load profile data. Please refresh the page.',
          source: 'Profile',
          priority: 'medium'
        });
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [onAddAlert]);
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: 'User',
    organization: '',
    organizationType: 'organization',
    joinDate: '',
    lastLogin: '',
    address: ''
  });
  const [loading, setLoading] = useState(true);

  const [stats, setStats] = useState({
    camerasManaged: 0,
    alertsHandled: 0,
    systemUptime: '0%',
    totalEvents: 0
  });

  const handleSave = async () => {
    try {
      // Update user profile via API
      await makeApiRequest('/api/auth/me', {
        method: 'PUT',
        body: JSON.stringify({
          firstName: profileData.firstName,
          lastName: profileData.lastName,
          phone: profileData.phone,
          organization: profileData.organization,
          address: profileData.address
        })
      });
      setIsEditing(false);
      onAddAlert({
        type: 'success',
        title: 'Profile Updated',
        message: 'Your profile has been updated successfully.',
        source: 'Profile',
        priority: 'low'
      });
    } catch (error) {
      console.error('Profile update error:', error);
      onAddAlert({
        type: 'error',
        title: 'Update Failed',
        message: 'Failed to update profile. Please try again.',
        source: 'Profile',
        priority: 'medium'
      });
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Reset any unsaved changes in a real app
  };

  const handleQuickAction = (action: string) => {
    console.log('Quick action clicked:', action);

    switch (action) {
      case 'cameras':
        onNavigateToTab?.('cameras');
        break;
      case 'history':
        onNavigateToTab?.('history');
        break;
      case 'security':
        // Could open security settings or show security log
        console.log('Opening security log...');
        break;
      default:
        console.log('Unknown quick action:', action);
    }
  };

  const [darkMode, setDarkMode] = useState(false); // Default to light mode

  // Initialize theme from localStorage and listen for theme changes
  useEffect(() => {
    // Initialize from localStorage
    const savedTheme = localStorage.getItem('csense-theme');
    if (savedTheme) {
      setDarkMode(savedTheme === 'dark');
    }

    // Listen for theme changes from CSENSELayout
    const handleThemeChange = (e: CustomEvent) => {
      setDarkMode(e.detail.theme === 'dark');
    };

    document.addEventListener('themeChange', handleThemeChange as EventListener);
    return () => {
      document.removeEventListener('themeChange', handleThemeChange as EventListener);
    };
  }, []);

  if (loading) {
    return (
      <div className={`p-4 md:p-6 space-y-4 md:space-y-6 ${darkMode ? 'bg-gray-950' : 'bg-white'} min-h-screen`}>
        <div className="animate-pulse">
          <div className={`h-8 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded w-1/4 mb-6`}></div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <div className={`${darkMode ? 'bg-gray-800' : 'bg-gray-100'} rounded-lg h-64`}></div>
              <div className={`${darkMode ? 'bg-gray-800' : 'bg-gray-100'} rounded-lg h-48`}></div>
            </div>
            <div className="space-y-6">
              <div className={`${darkMode ? 'bg-gray-800' : 'bg-gray-100'} rounded-lg h-32`}></div>
              <div className={`${darkMode ? 'bg-gray-800' : 'bg-gray-100'} rounded-lg h-48`}></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`p-4 md:p-6 space-y-4 md:space-y-6 ${darkMode ? 'bg-gray-950' : 'bg-white'} min-h-screen`}>
      <div className="flex items-center justify-between">
        <h1 className={`${darkMode ? 'text-white' : 'text-gray-800'} text-lg md:text-2xl font-medium hidden md:block`}>Profile</h1>
        <div className="flex items-center gap-2">
          {!isEditing ? (
            <Button onClick={() => setIsEditing(true)} className="bg-blue-600 hover:bg-blue-700">
              <Edit className="h-4 w-4 mr-2" />
              Edit Profile
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button onClick={handleSave} className="bg-green-600 hover:bg-green-700">
                <Save className="h-4 w-4 mr-2" />
                Save
              </Button>
              <Button onClick={handleCancel} variant="outline" className="border-gray-700">
                Cancel
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* Profile Information */}
        <div className="lg:col-span-2 space-y-6">
          <Card className={`${darkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
            <CardHeader>
              <CardTitle className={`${darkMode ? 'text-white' : 'text-gray-800'} flex items-center gap-2 font-medium`}>
                <User className="h-5 w-5" />
                Personal Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Profile Picture */}
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center">
                  <User className="h-8 w-8 text-gray-400" />
                </div>
                {isEditing && (
                  <Button variant="outline" className="border-gray-700">
                    <Upload className="h-4 w-4 mr-2" />
                    Change Photo
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName" className={`${darkMode ? 'text-white' : 'text-gray-800'} font-medium`}>First Name</Label>
                  <Input
                    id="firstName"
                    value={profileData.firstName}
                    onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })}
                    disabled={!isEditing}
                    className={`${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-800'} ${!isEditing ? 'opacity-70' : ''}`}
                  />
                </div>
                <div>
                  <Label htmlFor="lastName" className={`${darkMode ? 'text-white' : 'text-gray-800'} font-medium`}>Last Name</Label>
                  <Input
                    id="lastName"
                    value={profileData.lastName}
                    onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })}
                    disabled={!isEditing}
                    className={`${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-800'} ${!isEditing ? 'opacity-70' : ''}`}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email" className={`${darkMode ? 'text-white' : 'text-gray-800'} font-medium`}>Email Address</Label>
                <div className="relative">
                  <Mail className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                  <Input
                    id="email"
                    type="email"
                    value={profileData.email}
                    onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                    disabled={!isEditing}
                    className={`${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-800'} ${!isEditing ? 'opacity-70' : ''} pl-10`}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="phone" className={`${darkMode ? 'text-white' : 'text-gray-800'} font-medium`}>Phone Number</Label>
                <div className="relative">
                  <Phone className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                  <Input
                    id="phone"
                    value={profileData.phone}
                    onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                    disabled={!isEditing}
                    className={`${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-800'} ${!isEditing ? 'opacity-70' : ''} pl-10`}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="role" className={`${darkMode ? 'text-white' : 'text-gray-800'} font-medium`}>Role</Label>
                <Input
                  id="role"
                  value={profileData.role}
                  disabled
                  className={`${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-100 border-gray-300 text-gray-500'}`}
                />
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'} mt-1`}>Contact your administrator to change your role</p>
              </div>
            </CardContent>
          </Card>

          {/* Organization Information */}
          <Card className={`${darkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
            <CardHeader>
              <CardTitle className={`${darkMode ? 'text-white' : 'text-gray-800'} flex items-center gap-2 font-medium`}>
                {profileData.organizationType === 'organization' ? (
                  <Building className="h-5 w-5" />
                ) : (
                  <Home className="h-5 w-5" />
                )}
                {profileData.organizationType === 'organization' ? 'Organization' : 'Household'} Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="organization" className={`${darkMode ? 'text-white' : 'text-gray-800'} font-medium`}>
                  {profileData.organizationType === 'organization' ? 'Organization Name' : 'Household Name'}
                </Label>
                <Input
                  id="organization"
                  value={profileData.organization}
                  onChange={(e) => setProfileData({ ...profileData, organization: e.target.value })}
                  disabled={!isEditing}
                  className={`${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-800'} ${!isEditing && darkMode ? 'opacity-70' : !isEditing ? 'bg-gray-100 text-gray-500' : ''}`}
                />
              </div>

              <div>
                <Label htmlFor="address" className={`${darkMode ? 'text-white' : 'text-gray-800'} font-medium`}>Address</Label>
                <div className="relative">
                  <MapPin className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                  <Input
                    id="address"
                    value={profileData.address}
                    onChange={(e) => setProfileData({ ...profileData, address: e.target.value })}
                    disabled={!isEditing}
                    className={`${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-800'} ${!isEditing ? 'opacity-70' : ''} pl-10`}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Security Settings */}
          <Card className={`${darkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
            <CardHeader>
              <CardTitle className={`${darkMode ? 'text-white' : 'text-gray-800'} flex items-center gap-2 font-medium`}>
                <Shield className="h-5 w-5" />
                Security Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button variant="outline" className={`w-full ${darkMode ? 'border-gray-700 text-gray-300 hover:text-white hover:bg-gray-800' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}>
                <Key className="h-4 w-4 mr-2" />
                Change Password
              </Button>

              <div className="flex items-center justify-between">
                <div>
                  <Label className={`${darkMode ? 'text-white' : 'text-gray-800'} font-medium`}>Two-Factor Authentication</Label>
                  <p className="text-sm text-gray-400">Not enabled</p>
                </div>
                <Button size="sm" className="bg-green-600 hover:bg-green-700">
                  Enable
                </Button>
              </div>

              <Separator className="bg-gray-700" />

              <div className="space-y-2">
                <Label className={`${darkMode ? 'text-white' : 'text-gray-800'} font-medium`}>Recent Activity</Label>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-gray-400">
                    <span>Last login</span>
                    <span>{new Date(profileData.lastLogin).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-gray-400">
                    <span>Account created</span>
                    <span>{new Date(profileData.joinDate).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Statistics and Quick Info */}
        <div className="space-y-6">
          <Card className={`${darkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
            <CardHeader>
              <CardTitle className={`${darkMode ? 'text-white' : 'text-gray-800'} font-medium`}>Account Status</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="bg-green-500/20 text-green-400 border-green-500">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Active
                </Badge>
                <Badge variant="outline" className="bg-blue-500/20 text-blue-400 border-blue-500">
                  {profileData.organizationType === 'organization' ? 'Business' : 'Personal'}
                </Badge>
              </div>

              <Separator className="bg-gray-700" />

              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-400">Member since</span>
                  <span className={`${darkMode ? 'text-white' : 'text-gray-800'}`}>{new Date(profileData.joinDate).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-400">Last seen</span>
                  <span className={`${darkMode ? 'text-white' : 'text-gray-800'}`}>Online now</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={`${darkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
            <CardHeader>
              <CardTitle className={`${darkMode ? 'text-white' : 'text-gray-800'} font-medium`}>System Statistics</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <Camera className="h-4 w-4 text-blue-400" />
                    <span className={`text-2xl ${darkMode ? 'text-white' : 'text-gray-800'}`}>{stats.camerasManaged}</span>
                  </div>
                  <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Cameras</p>
                </div>

                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <Shield className="h-4 w-4 text-red-400" />
                    <span className={`text-2xl ${darkMode ? 'text-white' : 'text-gray-800'}`}>{stats.alertsHandled}</span>
                  </div>
                  <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Alerts Handled</p>
                </div>
              </div>

              <Separator className={`${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />

              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>System Uptime</span>
                  <span className="text-green-500">{stats.systemUptime}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Events</span>
                  <span className={`${darkMode ? 'text-white' : 'text-gray-800'}`}>{stats.totalEvents.toLocaleString()}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={`${darkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
            <CardHeader>
              <CardTitle className={`${darkMode ? 'text-white' : 'text-gray-800'} font-medium`}>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                variant="outline"
                className={`w-full text-sm ${darkMode ? 'border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-white' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}
                onClick={() => handleQuickAction('cameras')}
              >
                <Camera className="h-4 w-4 mr-2" />
                View My Cameras
              </Button>
              <Button
                variant="outline"
                className={`w-full text-sm ${darkMode ? 'border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-white' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}
                onClick={() => handleQuickAction('history')}
              >
                <Clock className="h-4 w-4 mr-2" />
                Recent Activity
              </Button>
              <Button
                variant="outline"
                className="w-full border-gray-700 text-gray-300 text-sm hover:bg-gray-800 hover:text-white"
                onClick={() => handleQuickAction('security')}
              >
                <Shield className="h-4 w-4 mr-2" />
                Security Log
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}